import { Dialog } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { AddToCollection } from "@/shared/components/collections";
import type { SelectionOption } from "@/shared/components/ResourceView";
import { PlaylistAddIcon, VisibilityIcon } from "@/shared/foundation/icons/mui";
import { usePopupState } from "@/shared/hooks/usePopupState";

import type { DatasetRow } from "./DatasetsList";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
export interface PendingDatasetAction {
  ids: string[];
  clearSelection: () => void;
}

export interface DatasetBulkActions {
  /** "Add to Collection" and "Visualize", for `<DatasetsList selectionOptions>`. */
  selectionOptions: SelectionOption<DatasetRow>[];
  /** The collection picker "Add to Collection" opens; render it once beside the list. */
  addToCollectionDialog: React.ReactNode;
}

/**
 * Bulk actions for a `<DatasetsList>` selection: add the selected datasets
 * to a collection, or open them in the visualizer. A page appends its own
 * options (the Datasets page adds Delete) after these.
 *
 * "Add to Collection" clears the selection once the datasets are added;
 * "Visualize" navigates away, so it leaves the selection alone.
 */
export function useDatasetBulkActions(): DatasetBulkActions {
  const { goto } = useNavigation();

  const addToCollectionPopup = usePopupState<PendingDatasetAction>();
  const { open: openAddToCollectionPopup } = addToCollectionPopup;
  const pendingAdd = addToCollectionPopup.metadata;

  const selectionOptions = React.useMemo<SelectionOption<DatasetRow>[]>(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            metadata: {
              ids: selected.map((d) => d.dataset_id),
              clearSelection,
            },
          });
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { datasets: selected.map((d) => d.dataset_id) },
          });
        },
      },
    ],
    [openAddToCollectionPopup, goto],
  );

  // MUI's Dialog rather than the foundation one: the create-collection form's
  // tag suggestions portal at z-index 1300, which the foundation dialog's 1400
  // would cover.
  const addToCollectionDialog = (
    <Dialog
      open={addToCollectionPopup.isOpen}
      onClose={addToCollectionPopup.close}
    >
      <AddToCollection
        selected={{
          type: "Dataset",
          items: new Set(pendingAdd?.ids ?? []),
        }}
        onCancel={addToCollectionPopup.close}
        onSuccess={() => pendingAdd?.clearSelection()}
      />
    </Dialog>
  );

  return { selectionOptions, addToCollectionDialog };
}
